/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Video,
  Headphones,
  Users,
  BookOpen,
  Heart,
  DollarSign,
  Sparkles,
  MessageCircle,
  Mic,
  ExternalLink,
  Brain,
  Dumbbell,
  Compass,
  Landmark,
  ArrowRight,
  Check,
  Flame,
  CalendarCheck,
  Quote,
} from 'lucide-react';
import PortalSubPage, { ComingSoonCard } from '../PortalSubPage';
import {
  NEXT_MEETING,
  INTEREST_MAILTO,
  INTEREST_EMAIL,
  CIRCLE_FOCUS,
  CIRCLE_GUEST_SPEAKER,
  CIRCLE_TOPICS,
  CIRCLE_PRICE,
  getReflectionForOffset,
} from '../../data/portalData';

export function PortalMonthlyMeeting() {
  return (
    <PortalSubPage
      eyebrow="Brotherhood Circle"
      title={<>The <span className="italic text-[#D4AF37]">Monthly Meeting.</span></>}
      intro={`Last Saturday of every month at 10:00 AM EST, through June 2027. $${CIRCLE_PRICE} secures your seat and gets the Google Meet link sent straight to your inbox.`}
      icon={Calendar}
    >
      {/* Next up + registration CTA */}
      <div
        className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 via-[#8C6239]/10 to-transparent border border-[#D4AF37]/30 p-8 md:p-10"
        data-testid="monthly-next-up"
      >
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-3">
          {NEXT_MEETING.concluded ? 'Series concluded' : 'Next Circle'}
        </p>
        <h2 className="font-cormorant text-4xl font-medium text-white mb-3">
          {NEXT_MEETING.title}
        </h2>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-zinc-300 font-manrope text-sm mb-6">
          <span>&#128197; {NEXT_MEETING.date}</span>
          <span>&#128337; {NEXT_MEETING.time}</span>
          <span>&#128187; Google Meet (link emailed after payment)</span>
        </div>
        {!NEXT_MEETING.concluded && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/portal/monthly-meeting/register"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-8 py-3.5 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors"
              data-testid="monthly-register-btn"
            >
              Register &middot; ${CIRCLE_PRICE}
            </Link>
            <p className="text-zinc-500 font-manrope text-xs">
              Secure Stripe checkout &middot; Google Meet link emailed after payment
            </p>
          </div>
        )}
      </div>

      {/* Focus banner */}
      <div
        className="rounded-2xl bg-[#111111] border border-white/10 p-6 md:p-8"
        data-testid="circle-focus"
      >
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
          September focus
        </p>
        <h3 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-2">
          {CIRCLE_FOCUS.headline}
        </h3>
        <p className="text-zinc-400 font-manrope">{CIRCLE_FOCUS.intro}</p>
      </div>

      {/* Guest speaker */}
      <div
        className="rounded-2xl bg-[#111111] border border-white/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
        data-testid="guest-speaker"
      >
        {CIRCLE_GUEST_SPEAKER.image ? (
          <img
            src={CIRCLE_GUEST_SPEAKER.image}
            alt={CIRCLE_GUEST_SPEAKER.name}
            className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover border border-[#D4AF37]/40 flex-shrink-0"
            data-testid="guest-speaker-photo"
          />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
            <Mic className="w-8 h-8 text-[#D4AF37]" />
          </div>
        )}
        <div className="flex-1">
          <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
            Guest Speaker &middot; {CIRCLE_GUEST_SPEAKER.monthLabel || 'Next Circle'}
          </p>
          <h3 className="font-cormorant text-2xl md:text-3xl font-medium text-white mb-1">
            {CIRCLE_GUEST_SPEAKER.name}
          </h3>
          <p className="text-zinc-500 font-manrope text-sm italic mb-1">
            {CIRCLE_GUEST_SPEAKER.handle}
          </p>
          {CIRCLE_GUEST_SPEAKER.credentials && (
            <p className="text-[#D4AF37]/80 font-manrope text-xs tracking-wider mb-1">
              {CIRCLE_GUEST_SPEAKER.credentials}
            </p>
          )}
          {CIRCLE_GUEST_SPEAKER.role && (
            <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest mb-3">
              {CIRCLE_GUEST_SPEAKER.role}
            </p>
          )}
          <p className="text-zinc-300 font-manrope text-sm md:text-base leading-relaxed mb-3">
            {CIRCLE_GUEST_SPEAKER.bio_long || CIRCLE_GUEST_SPEAKER.bio}
          </p>
          {CIRCLE_GUEST_SPEAKER.quote && (
            <p className="font-cormorant italic text-white text-lg md:text-xl border-l-2 border-[#D4AF37] pl-3 mb-4">
              &ldquo;{CIRCLE_GUEST_SPEAKER.quote}&rdquo;
            </p>
          )}
          <a
            href={CIRCLE_GUEST_SPEAKER.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:underline"
            data-testid="guest-speaker-link"
          >
            View profile <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Topics */}
      <div data-testid="circle-topics">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-cormorant text-2xl md:text-3xl font-medium text-white">
            September topics
          </h3>
          <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
            {CIRCLE_TOPICS.length} conversations
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {CIRCLE_TOPICS.map((t, i) => (
            <div
              key={i}
              className="rounded-2xl bg-[#111111] border border-white/10 p-5 hover:border-[#D4AF37]/40 transition-colors"
              data-testid={`circle-topic-${i}`}
            >
              <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-widest font-bold mb-2">
                Topic {i + 1}
              </p>
              <h4 className="font-cormorant text-xl font-medium text-white mb-2">
                {t.title}
              </h4>
              <p className="text-zinc-400 font-manrope text-sm leading-relaxed">
                {t.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Cadence */}
      <div className="rounded-2xl bg-[#111111] border border-white/10 p-6">
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold mb-3">
          Cadence
        </p>
        <p className="text-white font-cormorant text-2xl font-medium mb-2">
          Last Saturday of every month
        </p>
        <p className="text-zinc-400 font-manrope text-sm">
          10:00 AM EST · 60 minutes · running through June 2027.
        </p>
      </div>
    </PortalSubPage>
  );
}
